/* eslint-disable */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import FeedbackPrompt from "../components/FeedbackPrompt";
import BookNowV2 from "../components/BookNowV2";
import { useAuth } from "../lib/auth";
import { FeaturedBadge } from "../components/FeaturedBadge";
import MemberAvatar from "../components/MemberAvatar";
import SaveSpaceButton from "../components/SaveSpaceButton";
import WorkspaceFallbackTile from "../components/WorkspaceFallbackTile";

const AMENITY_LABELS = { wifi: "WiFi", standingDesk: "Standing Desk", phoneBooth: "Phone Booth", parking: "Parking" };

const RESOURCE_TYPE_LABELS = {
  day_pass:        "Day Pass",
  hot_desk:        "Hot Desk",
  dedicated_desk:  "Dedicated Desk",
  private_office:  "Private Office",
  team_office:     "Team Office",
  office_suite:    "Office Suite",
  meeting_room:    "Meeting Room",
  conference_room: "Conference Room",
  podcast_room:    "Podcast Room",
  training_room:   "Training Room",
  phone_booth:     "Phone Booth",
  project_room:    "Project Room",
  event_space:     "Event Space",
  custom:          "Workspace",
};

const PRICE_UNIT_LABEL = { hour: "/hour", day: "/day", half_day: "/half-day", week: "/week", month: "/month", inquiry: "" };

function formatStartingPrice(r) {
  const cents = r.starting_price_cents;
  if (cents == null || cents === 0) return "Contact for pricing";
  const unit = PRICE_UNIT_LABEL[r.starting_price_unit] || "";
  return `From $${(cents / 100).toFixed(0)}${unit}`;
}

function PublicHeader() {
  // Session 48 · Auth-aware header. Previously always showed "Member sign in"
  // which made logged-in members think they had been signed out when they
  // opened a listing. Now mirrors the AppShell nav for signed-in users.
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();
  return (
    <header className="rec-topbar">
      <Link to="/spaces" className="rec-brand" data-testid="space-brand">
        <span className="rec-brand-mark"><img src="/brand/metier-icon.png" alt="Métier" /></span>
      </Link>
      <nav className="rec-topnav">
        <Link to="/spaces">Spaces</Link>
        <Link to="/list-your-space">List your space</Link>
        {user ? (
          <>
            <Link to="/profile" data-testid="space-user-chip" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <MemberAvatar user={user} size={26} clickable={false} />
              <span style={{ fontSize: 13 }}>{(user.name || user.email || "").split(" ")[0]}</span>
            </Link>
            <button
              type="button"
              onClick={async () => { await logout?.(); nav("/login"); }}
              data-testid="space-signout"
              style={{
                background: "transparent", border: "none",
                color: "inherit", cursor: "pointer",
                font: "inherit", padding: 0,
                letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 11,
              }}
            >
              Sign out
            </button>
          </>
        ) : (
          <Link to="/login" data-testid="space-signin">Member sign in</Link>
        )}
      </nav>
    </header>
  );
}

export default function SpaceDetail() {
  const { slug } = useParams();
  const { user } = useAuth() || {};
  const [s, setS] = useState(null);
  const [active, setActive] = useState(0);
  const [err, setErr] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [showBookV2, setShowBookV2] = useState(false);
  // Parent-venue children (Inventory V2 — Session B). If /api/venues/:slug
  // returns a venue with published child resources, we show the "Available
  // Workspaces" section between amenities and rates.
  const [children, setChildren] = useState(null);
  const [childFilter, setChildFilter] = useState("all");

  useEffect(() => {
    setS(null); setErr(""); setChildren(null);
    api.get(`/spaces/${slug}`).then(r => setS(r.data)).catch(() => setErr("Listing not found."));
    // Fire the venue lookup in parallel — silently no-op for legacy listings
    // that don't have a parent-venue entry in Inventory V2.
    api.get(`/venues/${slug}`).then(r => setChildren(r.data)).catch(() => setChildren({ workspaces: [], venues: [], counts: {total: 0} }));
  }, [slug]);

  // Dwell-time trigger: prompt after 22s on-page (once per session per slug).
  useEffect(() => {
    if (!slug) return;
    const key = `metier_fb_dismissed_view_${slug}`;
    try { if (sessionStorage.getItem(key) === "1") return; } catch { return; }
    const t = setTimeout(() => setShowFeedback(true), 22_000);
    return () => clearTimeout(t);
  }, [slug]);

  if (err) return <div className="rec-page"><PublicHeader /><div className="rec-main"><h1 className="rec-h1">Not <em>found</em>.</h1><p className="rec-desc">{err} <Link to="/spaces" style={{color:"var(--gold)"}}>Back to all spaces →</Link></p></div></div>;
  if (!s) return <div className="rec-page"><PublicHeader /><div className="m-loading-inline">Loading workspace…</div></div>;

  // Session 53.7 · Hero source falls back gallery → photos → img so admin
  // uploads (which write to `photos[]` via AdminPhotoUploader) and legacy
  // RecMode gallery entries + `img` scalar all render the hero.
  const gallery = (s.gallery && s.gallery.length ? s.gallery
                    : (s.photos && s.photos.length ? s.photos
                        : (s.img ? [s.img] : []))) || [];

  return (
    <div className="rec-page">
      <PublicHeader />
      <main className="rec-main">
        <div className="rec-eyebrow">Métier · Workspace</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h1 className="rec-h1" data-testid="space-name" style={{ margin: 0 }}>{s.name}</h1>
          <FeaturedBadge workspace={s} />
          {user && (
            <SaveSpaceButton
              spaceType="workspace"
              spaceId={s.slug || s.workspace_id || s.location_id}
              spaceName={s.name}
              variant="pill"
            />
          )}
          {/* Session 47 · Admin/owner shortcut back into the edit UI.
              Only rendered for signed-in admins; owner-level access is
              enforced server-side on the edit route so this is safe. */}
          {user?.role === "admin" && (
            <Link
              to={
                s.is_venue_parent
                  ? `/admin/venues/${s.location_id || s.slug}/edit`
                  : `/admin/workspaces/${s.workspace_id || s.slug}/edit`
              }
              data-testid="space-manage-listing"
              style={{
                marginLeft: "auto",
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 12px", fontSize: 11,
                letterSpacing: "0.14em", textTransform: "uppercase",
                fontWeight: 600,
                border: "1px solid var(--gold, #B8913A)",
                borderRadius: 999,
                color: "var(--gold, #B8913A)",
                textDecoration: "none",
                background: "rgba(184,145,58,0.06)",
              }}
              title="Open in Admin editor"
            >
              <span aria-hidden="true">⚙</span> Manage listing
            </Link>
          )}
        </div>
        <p className="rec-loc" style={{ marginTop: 8 }}>{s.neighborhood} · {s.city} · {s.type}</p>
        {s.address && (
          <p className="rec-address" data-testid="space-address">
            <span className="rec-address-label">Address ·</span>{" "}
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`} target="_blank" rel="noreferrer" data-testid="space-address-link">
              {s.address}
            </a>
          </p>
        )}

        <div className="rec-layout">
          <section className="rec-left">
            {gallery.length > 0 ? (
              <div className="rec-gallery" data-testid="space-gallery">
                <div className="rec-gallery-main">
                  <img
                    src={gallery[active]}
                    alt={s.name}
                    onError={(e) => {
                      // Session 60.24 · When a photo file is missing (e.g.
                      // after a redeploy that wiped ephemeral uploads),
                      // swap in the Métier-branded logo. Never Unsplash —
                      // never implies a photo of the venue.
                      if (!e.target.dataset.fallbackApplied) {
                        e.target.dataset.fallbackApplied = "1";
                        e.target.src = "/brand/metier-logo.png";
                        e.target.style.background = "#FAF8F3";
                        e.target.style.padding = "40px";
                        e.target.style.objectFit = "contain";
                      }
                    }}
                  />
                </div>
                {gallery.length > 1 && (
                  <div className="rec-gallery-thumbs">
                    {gallery.map((g, i) => (
                      <button key={i} className={"rec-thumb" + (i === active ? " active" : "")} onClick={() => setActive(i)} data-testid={`space-thumb-${i}`}>
                        <img
                          src={g}
                          alt={`thumb ${i+1}`}
                          onError={(e) => {
                            if (!e.target.dataset.fallbackApplied) {
                              e.target.dataset.fallbackApplied = "1";
                              e.target.src = "/brand/metier-icon.png";
                              e.target.style.background = "#1B2340";
                              e.target.style.padding = "8px";
                              e.target.style.objectFit = "contain";
                            }
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // No gallery at all — render the branded Métier fallback tile
              // instead of a stock photograph (per Session 60.24 CTO
              // directive). Never implies a photo of the physical venue.
              <div className="rec-gallery" data-testid="space-gallery-empty">
                <div className="rec-gallery-main" style={{ padding: 0 }}>
                  <WorkspaceFallbackTile
                    name={s.name}
                    location={[s.neighborhood, s.city].filter(Boolean).join(" · ")}
                    externalBooking={Boolean(s.external_booking || s.external_booking_url)}
                    variant="hero"
                  />
                </div>
              </div>
            )}

            <h2 className="rec-h2">About the <em>space</em></h2>
            <p className="rec-desc">{s.description}</p>

            {s.work_intent_tags && s.work_intent_tags.length > 0 && (
              <>
                <h3 className="rec-h3">Best for</h3>
                <div className="space-tag-grid" data-testid="space-best-for">
                  {s.work_intent_tags.map(t => (
                    <span key={t.key} className="space-tag space-tag-bestfor" data-testid={`bestfor-${t.key}`}>{t.label}</span>
                  ))}
                </div>
              </>
            )}

            {s.insights && s.insights.validated_for && s.insights.validated_for.length > 0 && (
              <>
                <h3 className="rec-h3">Validated by the community</h3>
                <div className="space-tag-grid" data-testid="space-validated-for">
                  {s.insights.validated_for.map(v => (
                    <span key={v.key} className="space-tag space-tag-validated" data-testid={`validated-${v.key}`}>
                      ✓ {v.label}
                      <small> · {v.avg_rating}★ from {v.review_count}</small>
                    </span>
                  ))}
                </div>
              </>
            )}

            {s.insights && s.insights.popular_with && s.insights.popular_with.length > 0 && (
              <>
                <h3 className="rec-h3">Popular with</h3>
                <div className="space-tag-grid" data-testid="space-popular-with">
                  {s.insights.popular_with.map(p => (
                    <span key={p.profession} className="space-tag space-tag-popular" data-testid={`popular-${p.profession}`}>
                      {p.label}
                      <small> · {p.count}+ stays</small>
                    </span>
                  ))}
                </div>
              </>
            )}

            {s.insights && s.insights.category_scores && Object.keys(s.insights.category_scores).length > 0 && (
              <>
                <h3 className="rec-h3">What members say</h3>
                {s.insights.overall_rating > 0 && (
                  <div className="space-overall-rating" data-testid="space-overall-rating">
                    <span className="space-overall-stars">{"★".repeat(Math.round(s.insights.overall_rating))}{"☆".repeat(5 - Math.round(s.insights.overall_rating))}</span>
                    <span className="space-overall-num"><em>{s.insights.overall_rating}</em>/5</span>
                    <span className="space-overall-count">{s.insights.review_count} review{s.insights.review_count === 1 ? "" : "s"}</span>
                  </div>
                )}
                <div className="space-cat-grid" data-testid="space-category-scores">
                  {Object.entries(s.insights.category_scores).map(([k, v]) => (
                    <div key={k} className="space-cat-row">
                      <span className="space-cat-label">{v.label}</span>
                      <div className="space-cat-bar">
                        <div className="space-cat-bar-fill" style={{ width: `${(v.avg / 5) * 100}%` }} />
                      </div>
                      <span className="space-cat-num">{v.avg.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {s.amenities && s.amenities.length > 0 && (
              <>
                <h3 className="rec-h3">Amenities</h3>
                <ul className="rec-amenities">
                  {s.amenities.map(a => (
                    <li key={a}><span className="rec-am-dot" />{AMENITY_LABELS[a] || a}</li>
                  ))}
                </ul>
              </>
            )}

            <h3 className="rec-h3">Rates</h3>
            <div className="rec-rates">
              {s.rates.hourly > 0 && <div><span className="rate-label">Hourly</span><span className="rate-val"><em>${s.rates.hourly}</em>/hr</span></div>}
              {s.rates.daily > 0 && <div><span className="rate-label">Daily</span><span className="rate-val"><em>${s.rates.daily}</em>/day</span></div>}
              {s.rates.weekly > 0 && <div><span className="rate-label">Weekly</span><span className="rate-val"><em>${s.rates.weekly}</em>/wk</span></div>}
              {s.rates.monthly > 0 && <div><span className="rate-label">Monthly</span><span className="rate-val"><em>${s.rates.monthly}</em>/mo</span></div>}
            </div>

            {children && children.counts && children.counts.total > 0 && (
              <VenueWorkspacesSection
                venueSlug={s.slug}
                data={children}
                activeFilter={childFilter}
                onFilterChange={setChildFilter}
              />
            )}

            {/* Session 58.7 · P1 · Upcoming events at this venue. Backend
                filters for published+upcoming+not-canceled and prefers
                explicit venue_id linkage. Hidden entirely when empty. */}
            {children && Array.isArray(children.upcoming_events) && children.upcoming_events.length > 0 && (
              <VenueUpcomingEvents events={children.upcoming_events} venueName={s.name} />
            )}
          </section>

          <aside className="rec-widget">
            <div className="rec-widget-card">
              {/* Session 49 · External booking gets first priority — if
                  the workspace/venue is configured for an external URL,
                  show a "Book Direct" CTA with UTM tracking. */}
              {s.external_booking_url ? (
                <>
                  {s.rates?.hourly > 0 && (
                    <div className="rec-widget-price"><em>${s.rates.hourly}</em><span>/hour starting</span></div>
                  )}
                  <a
                    href={s.external_booking_url}
                    target={s.open_in_new_tab === false ? "_self" : "_blank"}
                    rel="noopener noreferrer"
                    className="m-btn-gold w-full"
                    style={{display:"block",textAlign:"center",width:"100%",textDecoration:"none"}}
                    data-testid="space-book-external"
                    onClick={() => {
                      try {
                        fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/track/partner-click`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            workspace_slug: s.slug,
                            source: "space_detail",
                            page_source: "space_detail_widget",
                            destination_url: s.external_booking_url,
                          }),
                        });
                      } catch { /* noop */ }
                    }}
                  >
                    {s.booking_cta_override || "Book Direct →"}
                  </a>
                  <p className="rec-secure" style={{marginTop:10}}>
                    {s.cta_disclosure || "You'll complete your reservation securely on our partner's website."}
                  </p>
                </>
              ) : s.bookable_publicly ? (
                <>
                  <div className="rec-widget-price"><em>${s.rates.hourly}</em><span>/hour starting</span></div>
                  {!showBookV2 ? (
                    <button type="button" className="m-btn-gold w-full"
                      style={{display:"block",textAlign:"center",width:"100%"}}
                      onClick={() => setShowBookV2(true)}
                      data-testid="space-book-public">
                      Book with Métier →
                    </button>
                  ) : (
                    <BookNowV2
                      workspaceId={s.workspace_id || s.slug}
                      workspaceName={s.name}
                      defaultEmail={user?.email || ""}
                      defaultName={user?.name || ""}
                      hourly={s.rates?.hourly}
                      daily={s.rates?.daily}
                      paymentSchedule={s.payment_schedule}
                      venueTimezone={s.venue_timezone}
                    />
                  )}
                  <p className="rec-secure">Pay securely with any card. Bookings are processed directly through the host via Stripe.</p>
                </>
              ) : user && s.rates?.hourly > 0 ? (
                // Session 50 · Signed-in members should always be able to
                // start a booking request. Previously they hit the same
                // "Members only — Sign in to reserve" wall as anonymous
                // visitors when Stripe onboarding hadn't been finalized,
                // which looked like the app had signed them out.
                <>
                  <div className="rec-widget-price"><em>${s.rates.hourly}</em><span>/hour starting</span></div>
                  {!showBookV2 ? (
                    <button type="button" className="m-btn-gold w-full"
                      style={{display:"block",textAlign:"center",width:"100%"}}
                      onClick={() => setShowBookV2(true)}
                      data-testid="space-book-request">
                      Request to book →
                    </button>
                  ) : (
                    <BookNowV2
                      workspaceId={s.workspace_id || s.slug}
                      workspaceName={s.name}
                      defaultEmail={user?.email || ""}
                      defaultName={user?.name || ""}
                      hourly={s.rates?.hourly}
                      daily={s.rates?.daily}
                      paymentSchedule={s.payment_schedule}
                      venueTimezone={s.venue_timezone}
                    />
                  )}
                  <p className="rec-secure" style={{marginTop:10}}>
                    Requests are reviewed by the host. You'll be charged only after approval.
                  </p>
                </>
              ) : user ? (
                // Session 51 · Signed-in member on a listing with no
                // hourly rate (venue-parent, legacy listing, etc.). Never
                // show them the anonymous "Join network" wall — surface
                // a friendly "Contact host" fallback so the flow doesn't
                // dead-end.
                <>
                  <div className="rec-eyebrow" style={{marginBottom:8}}>Booking</div>
                  <h3 className="rec-h2" style={{margin:"0 0 12px",fontSize:22}}>Reach out to <em>reserve</em>.</h3>
                  <p className="rec-desc" style={{margin:"0 0 18px",fontSize:14}}>
                    Pricing isn't published for this listing yet. Send the host a note and they'll confirm availability directly.
                  </p>
                  <a
                    href={`mailto:contact@workmetier.com?subject=${encodeURIComponent(`Reservation inquiry: ${s.name}`)}&body=${encodeURIComponent(`Hi — I'd like to reserve ${s.name}.\n\nDate:\nTime:\nDuration:\n\nThanks,\n${user.name || user.email}`)}`}
                    className="m-btn-gold w-full"
                    style={{display:"block",textAlign:"center",textDecoration:"none"}}
                    data-testid="space-cta-contact-host"
                  >
                    Contact host →
                  </a>
                </>
              ) : children && children.counts && children.counts.total > 0 ? (
                // Session 43 · Parent venues with child workspaces — steer
                // members to the child listings instead of a dead-end
                // "Members only" CTA. Fixes the "can't access Rec-Mode
                // facility" complaint by making the entrance obvious.
                <>
                  <div className="rec-eyebrow" style={{marginBottom:8}}>Venue</div>
                  <h3 className="rec-h2" style={{margin:"0 0 12px",fontSize:26}}>{children.counts.total} bookable {children.counts.total === 1 ? "workspace" : "workspaces"} <em>here</em>.</h3>
                  <p className="rec-desc" style={{margin:"0 0 18px",fontSize:14}}>Choose the room or event space below to see live pricing and reserve directly.</p>
                  <a
                    href="#venue-children"
                    onClick={(e) => { e.preventDefault(); const el = document.getElementById("venue-children"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    className="m-btn-gold w-full"
                    style={{display:"block",textAlign:"center",textDecoration:"none"}}
                    data-testid="space-see-children"
                  >
                    See available workspaces ↓
                  </a>
                </>
              ) : (
                <>
                  <div className="rec-eyebrow" style={{marginBottom:8}}>Members only</div>
                  <h3 className="rec-h2" style={{margin:"0 0 12px",fontSize:26}}>Sign in to <em>reserve</em>.</h3>
                  <p className="rec-desc" style={{margin:"0 0 18px",fontSize:14}}>This workspace is reserved for Métier members. Membership is free, join in 30 seconds.</p>
                  <Link to="/signup" className="m-btn-gold w-full" style={{display:"block",textAlign:"center",textDecoration:"none",marginBottom:10}} data-testid="space-cta-signup">
                    Join network
                  </Link>
                  <Link to="/login" className="m-btn-ghost" style={{display:"block",textAlign:"center",textDecoration:"none"}} data-testid="space-cta-signin">
                    Already a member? Sign in
                  </Link>
                </>
              )}
            </div>
          </aside>
        </div>
      </main>
      <footer className="rec-footer">
        <span>© Métier · workmetier.app · <Link to="/llm-info" style={{color:"var(--m-muted)",textDecoration:"none"}} data-testid="space-llm-info">For AI assistants</Link></span>
        <span><Link to="/list-your-space" style={{color:"var(--gold)"}}>Host your space on Métier →</Link></span>
      </footer>

      <FeedbackPrompt
        workspaceSlug={s.slug}
        workspaceName={s.name}
        context="view"
        visible={showFeedback}
        onDismiss={() => setShowFeedback(false)}
      />
    </div>
  );
}



// ============================================================
// VenueWorkspacesSection — Session B parent-venue child listing
// ============================================================
// Renders "Available Workspaces" under the venue detail, with tab filters
// grouped by resource type (Day Passes / Private Offices / Meeting Rooms /
// Event Spaces / Other). Each child links to /spaces/:venueSlug/:resourceSlug.
function VenueWorkspacesSection({ venueSlug, data, activeFilter, onFilterChange }) {
  const all = useMemo(() => [
    ...(data.workspaces || []).map(w => ({ ...w, _kind: "workspace" })),
    ...(data.venues || []).map(v => ({ ...v, _kind: "venue" })),
  ], [data]);

  const filters = useMemo(() => {
    const map = { all: all.length, day: 0, office: 0, meeting: 0, event: 0, other: 0 };
    all.forEach(r => {
      const t = r.workspace_type || r.venue_type || "";
      if (t === "day_pass" || t === "hot_desk" || t === "dedicated_desk") map.day += 1;
      else if (t === "private_office" || t === "team_office" || t === "office_suite") map.office += 1;
      else if (t === "meeting_room" || t === "conference_room" || t === "phone_booth") map.meeting += 1;
      else if (t === "event_space" || t === "workshop_space" || t === "brand_activation") map.event += 1;
      else map.other += 1;
    });
    return [
      { key: "all",     label: "All",              count: map.all },
      { key: "day",     label: "Day Passes",       count: map.day },
      { key: "office",  label: "Private Offices",  count: map.office },
      { key: "meeting", label: "Meeting Rooms",    count: map.meeting },
      { key: "event",   label: "Event Spaces",     count: map.event },
      { key: "other",   label: "Other",            count: map.other },
    ].filter(f => f.count > 0);
  }, [all]);

  const visible = useMemo(() => {
    if (activeFilter === "all") return all;
    const groups = {
      day:     new Set(["day_pass", "hot_desk", "dedicated_desk"]),
      office:  new Set(["private_office", "team_office", "office_suite"]),
      meeting: new Set(["meeting_room", "conference_room", "phone_booth"]),
      event:   new Set(["event_space", "workshop_space", "brand_activation"]),
    };
    return all.filter(r => {
      const t = r.workspace_type || r.venue_type || "";
      if (activeFilter === "other") return !Object.values(groups).some(s => s.has(t));
      return groups[activeFilter]?.has(t);
    });
  }, [all, activeFilter]);

  if (all.length === 0) return null;

  return (
    <div className="venue-children" data-testid="venue-children" id="venue-children">
      <h3 className="rec-h3">Available Workspaces</h3>
      <div className="venue-children-filters" role="tablist" data-testid="venue-children-filters">
        {filters.map(f => (
          <button
            key={f.key}
            className={"venue-children-filter" + (activeFilter === f.key ? " active" : "")}
            onClick={() => onFilterChange(f.key)}
            data-testid={`venue-children-filter-${f.key}`}
            role="tab"
            aria-selected={activeFilter === f.key}
          >
            {f.label} <em>{f.count}</em>
          </button>
        ))}
      </div>

      <div className="venue-children-grid">
        {visible.map(r => (
          <article key={r.resource_id} className="venue-child-card" data-testid={`venue-child-${r.slug}`}>
            {r.primary_image && (
              <div className="venue-child-image">
                <img src={r.primary_image} alt={r.name} loading="lazy" />
              </div>
            )}
            <div className="venue-child-body">
              <div className="venue-child-eyebrow">
                {RESOURCE_TYPE_LABELS[r.workspace_type || r.venue_type] || "Workspace"}
                {r.capacity > 0 && <span> · Up to {r.capacity} {r.capacity === 1 ? "person" : "people"}</span>}
              </div>
              <h4 className="venue-child-name">{r.name}</h4>
              <div className="venue-child-price">{formatStartingPrice(r)}</div>
              <div className="venue-child-actions">
                <Link
                  to={`/spaces/${venueSlug}/${r.slug}`}
                  className="m-btn-ghost small"
                  data-testid={`venue-child-view-${r.slug}`}
                >
                  View workspace
                </Link>
                {r.management_mode === "external_link" && r.booking_url_source ? (
                  <a
                    href={r.booking_url_source === "location_default"
                      ? `/spaces/${venueSlug}/${r.slug}#booking`
                      : `/spaces/${venueSlug}/${r.slug}#booking`}
                    className="m-btn-gold small"
                    data-testid={`venue-child-book-${r.slug}`}
                  >
                    Book direct →
                  </a>
                ) : (
                  <Link
                    to={`/spaces/${venueSlug}/${r.slug}#booking`}
                    className="m-btn-gold small"
                    data-testid={`venue-child-book-${r.slug}`}
                  >
                    Book now
                  </Link>
                )}
              </div>
            </div>
          </article>
        ))}
        {visible.length === 0 && (
          <div className="m-empty small" data-testid="venue-children-empty">
            No workspaces of this type at this venue yet.
          </div>
        )}
      </div>
    </div>
  );
}

// Session 58.7 · P1 · Upcoming Events at this venue.
// Renders a horizontal card list of the venue owner/org's published upcoming
// events. Section is fully hidden by the parent when there are 0 events.
// Sorted soonest first (server-side); dedup + published/status filtering
// happens server-side too. Aspect ratio preserved, no stretching.
function VenueUpcomingEvents({ events, venueName }) {
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", weekday: "short" });
    } catch { return ""; }
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };
  return (
    <div
      className="venue-upcoming-events"
      data-testid="venue-upcoming-events"
      style={{ marginTop: 40 }}
    >
      <div style={{ marginBottom: 14 }}>
        <span className="m-eyebrow" style={{ color: "var(--gold)" }}>UPCOMING · AT {venueName ? venueName.toUpperCase() : "THIS VENUE"}</span>
        <h3 className="rec-h2" style={{ margin: "4px 0 0", fontSize: 26 }}>Events <em>here</em>.</h3>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 16,
        }}
      >
        {events.map((ev) => (
          <Link
            key={ev.event_id}
            to={ev.url || `/community/events/${ev.event_id}`}
            data-testid={`venue-event-${ev.event_id}`}
            style={{
              textDecoration: "none",
              color: "inherit",
              background: "var(--navy-mid, #232b48)",
              border: "1px solid rgba(212,168,75,0.28)",
              borderRadius: 14,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transition: "transform .18s ease, box-shadow .18s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            {ev.image && (
              <div style={{ position: "relative", aspectRatio: "16/10", overflow: "hidden" }}>
                <img
                  src={ev.image}
                  alt={ev.title || "Event"}
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
            )}
            <div style={{ padding: "14px 16px", color: "#faf8f3" }}>
              <div style={{ fontSize: 11, letterSpacing: 0.14, textTransform: "uppercase", color: "var(--gold)", fontWeight: 700 }}>
                {fmtDate(ev.start_at)}{ev.start_at ? ` · ${fmtTime(ev.start_at)}` : ""}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, lineHeight: 1.15, marginTop: 6 }}>
                {ev.title || "Untitled Event"}
              </div>
              {ev.category && (
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6, letterSpacing: 0.14, textTransform: "uppercase" }}>
                  {ev.category}
                </div>
              )}
              <span style={{
                display: "inline-block", marginTop: 12, color: "var(--gold)",
                fontSize: 12, letterSpacing: 0.14, textTransform: "uppercase", fontWeight: 600,
              }}>RSVP →</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

